import { useState, useEffect, useRef } from 'react'
import { DatePicker } from '../ui/date-picker'

interface FilterOptions {
  status: string
  startDate: string
  endDate: string
  token?: string
}

interface StatusOption { value: string; label: string }

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  onApplyFilters: (filters: FilterOptions) => void
  currentFilters: FilterOptions
  statusOptions?: StatusOption[] // opções injetáveis
  defaultStatusValue?: string // valor usado no reset (ex: '-1', '4')
  statusLabel?: string // label custom do campo status
  enableTokenField?: boolean // exibe campo token (uso específico daily-sales)
}

export const FilterModal = ({ isOpen, onClose, onApplyFilters, currentFilters, statusOptions: injectedStatusOptions, defaultStatusValue = '4', statusLabel = 'Status', enableTokenField = false }: FilterModalProps) => {
  const [filters, setFilters] = useState<FilterOptions>(currentFilters)
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)
  const [dateError, setDateError] = useState<string>('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const statusOptions: StatusOption[] = injectedStatusOptions && injectedStatusOptions.length > 0 ? injectedStatusOptions : [
    { value: defaultStatusValue, label: 'Todos los estados' },
    { value: '1', label: 'Cerrada' },
    { value: '0', label: 'Abierta' },
    { value: '2', label: 'Cancelada' },
    { value: '3', label: 'En Progreso' }
  ]

  const getStatusLabel = (value: string) => {
    return statusOptions.find(option => option.value === value)?.label || 'Todos los estados'
  }

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false)
      }
    }

    if (isStatusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isStatusDropdownOpen])

  const handleApply = () => {
    // Validação de datas: se ambas existem e end < start => erro
    if (filters.startDate && filters.endDate) {
      if (new Date(filters.endDate) < new Date(filters.startDate)) {
        setDateError('La fecha final no puede ser anterior a la fecha inicial.')
        return
      }
    }
    setDateError('')
    onApplyFilters(filters)
    onClose()
  }

  const handleReset = () => {
    const resetFilters: FilterOptions = {
      status: defaultStatusValue, // Todos los estados
      startDate: '',
      endDate: '',
      token: enableTokenField ? '' : currentFilters.token
    }
    setFilters(resetFilters)
  setDateError('')
    onApplyFilters(resetFilters)
    onClose()
  }

  // Bloqueia scroll quando aberto
  useEffect(() => {
    if (!isOpen) return
    const scrollY = window.scrollY

    const original = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width
    }
    // Trava body mantendo posição
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'

    return () => {
      document.body.style.overflow = original.overflow
      document.body.style.position = original.position
      document.body.style.top = original.top
      document.body.style.width = original.width
      window.scrollTo(0, scrollY)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    // Container externo ocupa toda a viewport e centraliza o modal
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
        aria-hidden="true"
      />
      <div className="relative bg-white w-full max-w-md mx-auto rounded-2xl shadow-xl max-h-[90vh] flex flex-col animate-[fadeInScale_.18s_ease]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Cerrar"
            aria-label="Cerrar modal de filtros"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
  <div className="p-4 space-y-4 flex-1 overflow-visible">
      {/* Status */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
        {statusLabel}
            </label>
            <button
              type="button"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white text-left flex items-center justify-between"
            >
              <span className="text-gray-900">{getStatusLabel(filters.status)}</span>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isStatusDropdownOpen && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setFilters({ ...filters, status: option.value })
                      setIsStatusDropdownOpen(false)
                    }}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                      filters.status === option.value ? 'bg-orange-50 text-orange-600' : 'text-gray-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Fecha de Inicio */}
          <DatePicker
            label="Fecha de Inicio"
            value={filters.startDate}
            onChange={(value) => {
              setFilters({ ...filters, startDate: value })
              if (dateError && filters.endDate && value && new Date(filters.endDate) >= new Date(value)) {
                setDateError('')
              }
            }}
          />

          {/* Fecha de Fin */}
          <DatePicker
            label="Fecha de Fin"
            value={filters.endDate}
            onChange={(value) => {
              setFilters({ ...filters, endDate: value })
              if (dateError && filters.startDate && value && new Date(value) >= new Date(filters.startDate)) {
                setDateError('')
              }
            }}
          />
          {enableTokenField && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token
              </label>
              <input
                type="text"
                value={filters.token || ''}
                onChange={(e) => setFilters({ ...filters, token: e.target.value })}
                placeholder="Ingrese el token"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          )}
          {dateError && (
            <p className="text-sm text-red-600 font-medium -mt-2">{dateError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-2xl md:rounded-b-2xl flex-shrink-0">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Limpiar
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  )
}
